import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { sendEmail } from '../services/emailService';

const { FiMail, FiPhone, FiMapPin, FiSend } = FiIcons;

const ContactPage = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await sendEmail({
        formType: 'contact',
        ...formData
      });

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.message || 'Fehler beim Versenden der Nachricht');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <SafeIcon icon={FiSend} className="mr-3 text-accent-green" />
              {t('contact.sendMessage')}
            </h2>
            {isSubmitted && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 rounded-lg text-green-700">
                ✓ {t('contact.thankYou') || 'Nachricht erfolgreich versendet!'}
              
            {error && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 rounded-lg text-red-700">
                {error}
              </div>
            )}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('contact.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('contact.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('contact.subject')}
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('contact.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all resize-none"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 rounded-lg font-bold shadow-md transition-all ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'btn-custom-primary hover:shadow-lg'
                }`}
              >
                {isSubmitting ? '...' : t('contact.send')}
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            <div className="bg-primary-green text-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">
                {t('contact.contactInfo')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiMapPin} className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">{t('contact.address')}</h3>
                    <p className="text-white/90">Svansele 2</p>
                    <p className="text-white/90">936 93 Boliden</p>
                    <p className="text-white/90">Sverige</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiMail} className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">{t('contact.email')}</h3>
                    <a href="mailto:svanselemajeri@norsjo.nu" className="text-white/90 hover:text-white underline">
                      svanselemajeri@norsjo.nu
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiPhone} className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">{t('contact.phone')}</h3>
                    <p className="text-white/90">+46 (0)70 637 93 14</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 border-l-4 border-accent-green">
              <Link to="/storhandla" className="block hover:scale-105 transition-transform">
                <h2 className="text-xl font-bold text-gray-800 mb-4 hover:text-accent-green transition-colors">
                  {t('contact.wholesaleTitle')}
                </h2>
              </Link>
              <p className="text-gray-600 mb-4">
                {t('contact.wholesaleText')}
              </p>
              <ul className="space-y-2 text-gray-600">
                {(t('contact.wholesaleList', { returnObjects: true }) || []).map((item, index) => (
                  <li key={index} className="flex items-center">
                    <span className="w-2 h-2 bg-accent-green rounded-full mr-3"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;